/**
 * Builds a schema.org @graph for a content page (product-page / seller-page).
 *
 * Centralises Article + BreadcrumbList + FAQPage + ItemList so both page types
 * emit consistent, valid structured data. SSR-safe: pure string/object work,
 * no DOM access. Every section is optional — omit its input and the node is
 * simply not added, so it degrades cleanly until Phase 3 fills the new fields.
 *
 * @param {Object}  cfg
 * @param {string}  cfg.url            Canonical URL of the page (mainEntityOfPage / @id).
 * @param {string}  cfg.headline       Article headline (page H1 / title).
 * @param {string} [cfg.description]   Short description (meta description / TL;DR).
 * @param {string} [cfg.image]         Absolute image URL.
 * @param {string} [cfg.articleBody]   Full article body (HTML/text).
 * @param {string} [cfg.datePublished] ISO 8601 date.
 * @param {string} [cfg.dateModified]  ISO 8601 date.
 * @param {Object} [cfg.author]        { name, role } — rendered as schema.org Person.
 * @param {Object} [cfg.publisher]     { name, url, logo } — defaults to AnyKod.
 * @param {Array}  [cfg.breadcrumbs]   [{ name, url }] ordered root→current.
 * @param {Array}  [cfg.faq]           [{ q, a }] question/answer pairs.
 * @param {Object} [cfg.itemList]      { name, items:[...] } passed through verbatim.
 * @returns {Object} JSON-LD object with @context and @graph.
 */
export function buildContentPageGraph(cfg = {}) {
    const {
        url,
        headline,
        description,
        image,
        articleBody,
        datePublished,
        dateModified,
        author,
        publisher,
        breadcrumbs,
        faq,
        itemList,
    } = cfg;

    const graph = [];

    // --- Article ---------------------------------------------------------
    const article = {
        '@type': 'Article',
        headline,
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
        publisher: {
            '@type': 'Organization',
            name: publisher?.name ?? 'AnyKod',
            ...(publisher?.url ? { url: publisher.url } : {}),
            ...(publisher?.logo ? { logo: { '@type': 'ImageObject', url: publisher.logo } } : {}),
        },
    };
    if (description) article.description = description;
    if (image) article.image = image;
    if (articleBody) article.articleBody = articleBody;
    if (datePublished) article.datePublished = datePublished;
    // Fall back to publish date so dateModified is always present when we have either.
    if (dateModified || datePublished) article.dateModified = dateModified || datePublished;
    if (author?.name) {
        article.author = {
            '@type': 'Person',
            name: author.name,
            ...(author.role ? { jobTitle: author.role } : {}),
        };
    }
    graph.push(article);

    // --- BreadcrumbList ---------------------------------------------------
    if (Array.isArray(breadcrumbs) && breadcrumbs.length > 0) {
        graph.push({
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbs.map((crumb, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                name: crumb.name,
                item: crumb.url,
            })),
        });
    }

    // --- FAQPage ----------------------------------------------------------
    const faqItems = normaliseFaq(faq);
    if (faqItems.length > 0) {
        graph.push({
            '@type': 'FAQPage',
            mainEntity: faqItems.map((item) => ({
                '@type': 'Question',
                name: item.q,
                acceptedAnswer: { '@type': 'Answer', text: item.a },
            })),
        });
    }

    // --- ItemList (hot offers etc.) --------------------------------------
    if (itemList && Array.isArray(itemList.items) && itemList.items.length > 0) {
        graph.push({
            '@type': 'ItemList',
            name: itemList.name ?? 'Items',
            itemListElement: itemList.items,
        });
    }

    return { '@context': 'https://schema.org', '@graph': graph };
}

/**
 * Accepts FAQ as either an array of {q,a} / {question,answer} objects and
 * filters out incomplete entries. Returns a clean [{q,a}] array.
 *
 * @param {Array|null|undefined} faq
 * @returns {Array<{q: string, a: string}>}
 */
export function normaliseFaq(faq) {
    if (!Array.isArray(faq)) return [];
    return faq
        .map((item) => ({
            q: item?.q ?? item?.question ?? '',
            a: item?.a ?? item?.answer ?? '',
        }))
        .filter((item) => item.q && item.a);
}
